import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/i18n";
import { trackCta } from "@/lib/analytics";
import { ROUTES } from "@/config/links";

/**
 * Persistent "Book a Consultation" CTA visible while scrolling on every page.
 * - Desktop: bottom-right pill
 * - Mobile: full-width bottom bar with safe-area padding
 * - Appears after 480px scroll, hides on the /book* booking flow itself
 *   (SiteLayout already skips rendering this there — this guard is a
 *   defensive second layer for any future direct usage)
 * - Dismissable, session-remembered
 */

const STORAGE_KEY = "iag_stickycta_dismissed";

export const StickyBookCTA = ({ href = ROUTES.booking, targetService = "general_booking" }) => {
  const { t, l } = useLang();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setDismissed(sessionStorage.getItem(STORAGE_KEY) === "1");
    const onScroll = () => setVisible(window.scrollY > 480);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dismiss = () => {
    sessionStorage.setItem(STORAGE_KEY, "1");
    setDismissed(true);
  };

  if (dismissed) return null;
  const localPath = pathname.replace(/^\/(pt|el)(?=\/|$)/, "") || "/";
  if (localPath === ROUTES.booking || localPath.startsWith(`${ROUTES.booking}/`)) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="fixed z-[70] left-0 right-0 bottom-0 md:left-auto md:right-6 md:bottom-6 pointer-events-none"
          style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
          data-testid="sticky-book-cta"
        >
          <div className="pointer-events-auto md:w-auto w-full bg-paper md:bg-transparent border-t border-ink/12 md:border-0 shadow-[0_-8px_28px_rgba(27,27,24,0.10)] md:shadow-none px-4 py-3 md:p-0 flex items-center justify-between gap-3">
            <div className="hidden sm:block sm:flex-1 sm:min-w-0 md:hidden">
              <p className="font-serif text-base text-ink leading-tight">
                {t("stickycta.title")}
              </p>
              <p className="font-hand text-terracotta text-sm -mt-0.5">
                {t("stickycta.subtitle")}
              </p>
            </div>
            <Link
              to={l(href)}
              data-testid="sticky-book-cta-link"
              onClick={() => trackCta({ ctaId: "sticky-book-cta-link", location: "sticky_cta", href, targetService })}
              className="flex-1 min-w-0 md:flex-initial text-center md:text-left bg-ink text-paper px-5 py-3 md:px-7 md:py-4 font-body text-xs md:text-sm uppercase md:whitespace-nowrap tracking-[0.18em] shadow-[0_10px_28px_-10px_rgba(185,131,44,0.65)]"
            >
              {t("cta.book_consultation")}
            </Link>
            <button
              onClick={dismiss}
              data-testid="sticky-book-cta-dismiss"
              aria-label={t("stickycta.dismiss")}
              className="ml-1 w-9 h-9 flex items-center justify-center text-ink/60 hover:text-terracotta transition-colors shrink-0"
            >
              <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
                <path d="M4 4 L16 16 M16 4 L4 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
